/**
 * G：Story 成片通知的**本地模拟预览**载荷（仅管理后台评审用）。
 *
 * 隔离硬线（对应 03 场景 I）：
 * - 本模块是纯数据与纯函数：不读库、不写库、不发邮件、不触供应商、不扣费。
 * - 每张卡片 `simulated: true`，且 `play_mode: 'unavailable'`：成功卡片也没有任何可播地址，
 *   点击播放只会解释「模拟不可播放」，不是死链、也不是真实播放。
 * - 失败卡片不带退款金额/退款字段，绝不触发退款或消耗首免资格。
 * - 文案一律用 key（`title_key`/`summary_key`），由页面用 vue-i18n 渲染，模块内不写死用户可见文字。
 * - 卡片 id 由 kind 确定性生成：重复请求同一 kind 命中同一 id，只标记 duplicate，不产生新记录。
 */

import { renderStoryEpisode } from '@drama/email/templates/video-ready.js'

export const PREVIEW_KINDS = Object.freeze(['success', 'success_free', 'failed'])

const PREVIEW_CARDS = Object.freeze({
  success: Object.freeze({
    key: 'success',
    id: 'preview_story_film_episode_ready_success',
    type: 'story_film_episode_ready',
    resource_type: 'story_film_episode',
    resource_id: 'preview-episode-success',
    is_free: false,
    simulated: true,
    play_mode: 'unavailable',
    title_key: 'storyFilm.preview.sampleEpisodeTitle',
    summary_key: 'storyFilm.preview.sampleEpisodeSummary',
  }),
  success_free: Object.freeze({
    key: 'success_free',
    id: 'preview_story_film_episode_ready_success_free',
    type: 'story_film_episode_ready',
    resource_type: 'story_film_episode',
    resource_id: 'preview-episode-free',
    is_free: true,
    simulated: true,
    play_mode: 'unavailable',
    title_key: 'storyFilm.preview.sampleEpisodeTitle',
    summary_key: 'storyFilm.preview.sampleEpisodeSummary',
  }),
  failed: Object.freeze({
    key: 'failed',
    id: 'preview_story_film_episode_failed',
    type: 'story_film_episode_ready',
    resource_type: 'story_film_episode',
    resource_id: 'preview-episode-failed',
    is_free: false,
    simulated: true,
    play_mode: 'unavailable',
    failed_preview: true,
    title_key: 'storyFilm.preview.sampleEpisodeTitle',
    summary_key: 'storyFilm.preview.sampleEpisodeSummary',
  }),
})

/** @returns {Array<Record<string, any>>} 三态固定载荷（成功 / 首免成功 / 失败） */
export function previewCards() {
  return PREVIEW_KINDS.map(kind => ({ ...PREVIEW_CARDS[kind] }))
}

/**
 * 幂等的重复请求：同一 kind 永远返回同一 id；第二次起 `duplicate` 为真，页面据此提示「不重复通知」。
 * @param {string} kind
 * @param {Map<string, number>} [hits] 页面持有的命中计数（默认新建，便于单测）
 * @returns {{ id: string, kind: string, duplicate: boolean, hits: number }}
 */
export function requestPreview(kind, hits = new Map()) {
  if (!PREVIEW_KINDS.includes(kind)) throw new Error(`Unknown story film preview kind: ${kind}`)
  const card = PREVIEW_CARDS[kind]
  const count = (hits.get(kind) ?? 0) + 1
  hits.set(kind, count)
  return { id: card.id, kind, duplicate: count > 1, hits: count }
}

/** 禁止在预览载荷里出现真实可播地址或任何退款/扣费字段。 */
export function assertSimulatedOnly(cards) {
  if (!Array.isArray(cards) || cards.length === 0) throw new Error('Preview cards are required')
  for (const card of cards) {
    if (card.simulated !== true) throw new Error(`Preview card must be simulated: ${card.id}`)
    if (card.play_mode !== 'unavailable') throw new Error(`Preview card must not expose playback: ${card.id}`)
    for (const field of ['media_url', 'source_url', 'playback_url', 'private_url', 'refund_points', 'price_points', 'ledger_id']) {
      if (field in card) throw new Error(`Preview card must not carry ${field}: ${card.id}`)
    }
  }
  return true
}

/**
 * 六语对照：页面文案走 `@drama/i18n`（storyFilm.* 六语已落盘），邮件正文必须来自真实模板
 * `@drama/email` 的 `video-ready`（kind: 'story_episode'），本模块只渲染、不发送、不入库。
 */
export const PREVIEW_LOCALES = Object.freeze([
  Object.freeze({ code: 'en', label: 'English', email_locale: 'en' }),
  Object.freeze({ code: 'zh', label: '中文', email_locale: 'zh-CN' }),
  Object.freeze({ code: 'ja', label: '日本語', email_locale: 'ja' }),
  Object.freeze({ code: 'ko', label: '한국어', email_locale: 'ko' }),
  Object.freeze({ code: 'es', label: 'Español', email_locale: 'es' }),
  Object.freeze({ code: 'pt', label: 'Português', email_locale: 'pt' }),
])

/** 邮件预览用的固定样例地址：指向本人片库深链形状，但不带真实集 id，点开也不会命中任何真实集。 */
export const PREVIEW_EPISODE_URL = 'https://local.voko.world/my-videos/watch?storyEpisodeId=preview-sample'

/**
 * 用真实邮件模板渲染一集的完成提醒正文（不发送、不写库）。
 * @param {{ email_locale: string, episode_title: string, episode_summary: string, is_free?: boolean }} input
 * @returns {{ subject: string, text: string }}
 */
export function renderPreviewEmail({ email_locale, episode_title, episode_summary, is_free = false }) {
  if (typeof email_locale !== 'string' || email_locale === '') throw new Error('renderPreviewEmail: email_locale is required')
  const rendered = renderStoryEpisode({
    locale: email_locale,
    episodeTitle: episode_title,
    episodeSummary: episode_summary,
    episodeUrl: PREVIEW_EPISODE_URL,
    isFree: is_free === true,
  })
  return { subject: rendered.subject, text: rendered.text }
}

/** 邮件预览必须命中真实模板的六语，缺一个都要在页面显式标出，而不是静默回落英文。 */
export function missingEmailLocales() {
  const missing = []
  for (const locale of PREVIEW_LOCALES) {
    try {
      renderPreviewEmail({ email_locale: locale.email_locale, episode_title: 'x', episode_summary: 'y' })
    } catch {
      missing.push(locale.email_locale)
    }
  }
  return missing
}
